import {
  ServiceType,
  ServiceTypePatchDTO,
  useCreateServiceTypeMutation,
  useDeleteServiceTypeMutation,
  usePatchUpdateServiceTypeMutation,
  useUpdateServiceTypeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceTypeMutationProps = {}

export type UseServiceTypeMutationResult = {
  createServiceType: (serviceType: Partial<ServiceType>, callback: EntityMutationCallback<ServiceType> | undefined) => void
  updateServiceType: (serviceType: Partial<ServiceType>, callback: EntityMutationCallback<ServiceType> | undefined) => void
  deleteServiceType: (serviceTypeId: string, callback: EntityMutationCallback | undefined) => void

  patchUpdateServiceType: (id: string, patch: ServiceTypePatchDTO, callback: EntityMutationCallback<ServiceType> | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceTypeMutation = (props: UseServiceTypeMutationProps = {}): UseServiceTypeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceType, {isLoading: isCreating}] = useCreateServiceTypeMutation()
  const [_updateServiceType, {isLoading: isUpdating}] = useUpdateServiceTypeMutation()
  const [_deleteServiceType, {isLoading: isDeleting}] = useDeleteServiceTypeMutation()

  const [_patchUpdateServiceType, {isLoading: _isPatchUpdating}] = usePatchUpdateServiceTypeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating || _isPatchUpdating)
  }, [isUpdating, _isPatchUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceTypeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.servicetype", "Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceTypeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.servicetype", "Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceTypeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.servicetype", "Service Type"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceType>, callback: EntityMutationCallback<ServiceType> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetype", "Service Type") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceType> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetype", "Service Type") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetype", "Service Type") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceTypeId: string, callback: EntityMutationCallback<ServiceType> | undefined) => {
    const promise = _deleteServiceType({id: serviceTypeId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceType, handleDeleteResult])

  const createServiceType = useCallback((serviceType: Partial<ServiceType>, callback: EntityMutationCallback<ServiceType> | undefined) => {
    const promise = _createServiceType(serviceType).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceType, handleCreateResult])

  const updateServiceType = useCallback((serviceType: Partial<ServiceType>, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceType(serviceType).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceType, handleUpdateResult])

  const deleteServiceType = useCallback((serviceTypeId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.servicetype", "Service Type"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.servicetype.confirm", "Are you sure you want to delete this service type?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceTypeId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  const patchUpdateServiceType = useCallback((id: string, patch: ServiceTypePatchDTO, callback: EntityMutationCallback<ServiceType> | undefined) => {
    const promise = _patchUpdateServiceType({id, patch}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_patchUpdateServiceType, handleUpdateResult])

  return {
    createServiceType: createServiceType,
    updateServiceType: updateServiceType,
    deleteServiceType: deleteServiceType,

    patchUpdateServiceType: patchUpdateServiceType,

    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
